
// base stuff
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { v4 as generateUuid } from "uuid"

// apollo queries
import * as cdn from "@coredination/components"
import JobDetailQuery from "../../graphql/job/jobdetail.graphql"
import WorkReportsForJobQuery from "@/graphql/job/workReportsForJob.graphql"
import SetUserState from "../../graphql/job/setuserstate.graphql"
import PublishJob from "../../graphql/job/publishjob.graphql"
import EntityEventSubscription from "@/graphql/subscription/entityevent.graphql"
import GroupsQuery from "@/graphql/group/groups.graphql"
import UpdateJobItem from "@/graphql/job/updatejobitem.graphql"
import DeleteJobItem from "@/graphql/job/deletejobitem.graphql"
import CustomerProjectBrief from "@/graphql/customerproject/customerprojectbrief.graphql"
import Contract from "@/graphql/job/contract.graphql"
import UserDetail from "@/graphql/user/userdetail.graphql"
import CloseJob from "@/graphql/job/closejob.graphql"

import { CircularLoader, PullRefresh } from "@coredination/components"
import { Formatting } from "../../util/formatting"
import { Util } from "@/util/util"
import moment from "moment"
import { navbar, NavbarOption } from "@/components/NavbarOptions.vue"
import modaldialog from "@/components/dialogs/modaldialog"
const dateFormat: any = new Formatting.DateFormatting()
const util = new Util.Util()

@Component({
    apollo: {
        job: {
            query: JobDetailQuery,
            skip() {
                return !this.uuid
            },
            variables() {
                return { uuid: this.uuid }
            },
            error(error) {
                console.error("Error fetching job details:", error)
                this.fetchError = error.message
            },
        },
        workReportsForJob: {
            query: WorkReportsForJobQuery,
            variables() {
                return { jobUuid: this.model.uuid }
            },
            skip() {
                return !this.model || !this.model.uuid
            },
        },
        me: cdn.graphql.me,
        groups: GroupsQuery,
        customerProject: {
            query: CustomerProjectBrief,
            skip() {
                return !this.model || !this.model.customerProjectUuid
            },
            variables() {
                return { uuid: this.model.customerProjectUuid }
            }
        },
        contract: {
            query: Contract,
            skip() {
                return !this.model || !this.model.contractInfo  || !this.model.contractInfo.contractId
            },
            variables() {
                return { uuid: this.model.contractInfo.contractId.toString() }
            }
        },
        user: {
            query: UserDetail,
            skip() {
                return this.model.ordererFullName || !this.ordererId
            },
            variables() {
                return { uuid: this.ordererId }
            }
        },
        $subscribe: {
            entityEvent: {
                query: EntityEventSubscription,
                result(result: any) {
                    const event = result.data.entityEvent
                    if (
                        event.type == "entityUpdated" &&
                        event.data &&
                        event.data.entityClass == "Job" &&
                        event.data.uuid == this.model.uuid
                    ) {
                        console.log("job was updated", event.data)
                        if (this.job.version >= event.data.version) {
                            console.log("not refreshing - same version")
                        } else {
                            console.log("refreshing")
                            this.wsUpdate = true
                            this.refresh()
                        }
                    }
                },
                skip() {
                    return this.inhibitSubscription
                },
            },
        },
    },
    components: {
        CircularLoader,
        PullRefresh,
        StateField: () => import("@/components/fields/StateField.vue"),
        // form fields
        LabelField: () => import("@/components/fields/LabelField.vue"),
        DocumentsField: () => import("@/components/fields/DocumentsField.vue"),
        DestinationsField: () => import("@/components/fields/DestinationsField.vue"),
        WorkTaskField: () => import("@/components/fields/WorkTaskField.vue"),
        ResourceList: () => import("@/components/misc/ResourceList.vue"),
        ItemsTable: () => import("@/components/misc/ItemsTable.vue"),
        CustomFieldsField: () => import("@/components/fields/CustomFieldsField.vue"),
        JobCustomFormsField: () => import("@/components/fields/JobCustomFormsField.vue"),
        MyJobStateField: () => import("@/components/fields/MyJobStateField.vue"),
        JobNotesField: () => import("@/components/fields/JobNotesField.vue"),
        SignatureField: () => import("@/components/fields/SignatureField.vue"),
        RemoteSignatureField: () => import("@/components/fields/RemoteSignatureField.vue"),
        TransportDocumentsField: () => import("@/components/fields/TransportDocumentsField.vue"),
        // dialogs
        ShareDialog: () => import("@/components/dialogs/ShareDialog.vue"),
        SelectJobStateDialog: () => import("@/components/dialogs/SelectJobStateDialog.vue"),
        ContactField: () => import("@/components/fields/ContactField.vue"),
        JobEventsField: () => import("@/components/fields/JobEventsField.vue"),
        CreateSignatureTabDialog: () => import("@/components/dialogs/CreateSignatureTabDialog.vue"),
        CloseJobDialog: () => import("@/components/dialogs/CloseJobDialog.vue"),
    },
    beforeRouteLeave(to, from, next) {
        if (modaldialog.preventNavigation()) {
            console.log("modal dialog says no to navigation")
            return next(false)
        }
        next()
    },
})
export default class JobView extends Vue {
    @Prop()
    uuid?: string

    me: any = {}
    job: any = {}
    contract: any = {}
    customerProject?: any
    workReportsForJob: any[] = []
    groups?: any[]
    errorRequiredFields: any[] = []
    wsUpdate: boolean = false
    scheduleMe = false
    selectingExcludedFields = false
    selectingRequiredFields = false
    minDate: any = ""
    savingItems = false
    user: any = {}
    creatingSignature = false
    showCloseModal: boolean = false
    fetchError: string | null = null

    get inhibitSubscription() {
        return localStorage && !!localStorage.getItem("inhibitSubscription")
    }

    get excluded(): any {
        return this.model && this.model.excludedFields
            ? this.model.excludedFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }

    mounted() {
        this.$apollo.queries.job.refetch();
        if (this.job && this.job.uuid) {
            this.$apollo.queries.workReportsForJob.skip = false;
            this.$apollo.queries.workReportsForJob.refetch();
        }
    }

    @Watch('job.uuid')
    onJobUuidChange() {
        if (this.job.uuid) {
            this.$apollo.queries.workReportsForJob.skip = false;
            this.$apollo.queries.workReportsForJob.refetch();
        }
    }

    onScreenSignature(signature: any) {
        (this.$refs.signature as any)?.change(signature)
    }

    @Watch("me")
    @Watch("myResource")
    @Watch("job.state")
    watchOptions() {
        if (this.me != null) {
            navbar?.setOptions(this.options)
        }
    }

    @Watch("job.startTime")
    changedStartTime(event: any) {
        if (!event) this.minDate = ""
        var startTime = new Date(event)
        if (startTime) this.minDate = dateFormat.date(startTime, null)
    }

    canScheduleSelf() {
        return (
            this.hasGroupPermission("schedule_self") ||
            (this.hasGroupPermission("do_work") && this.me?.id == this.job?.creatorId) ||
            (this.hasGroupPermission("do_work") && this.hasGroupPermission("dispatch"))
        )
    }

    get fabOptions() {
        return [
            {
                title: this.$t("jobFabResource"),
                click: () => {
                    this.scrollIntoView(this.$refs.resources);
                    (this.$refs.resources as any).clickAddResource()
                },
                if: () =>
                    !this.isJobClosed &&
                    this.multipleResources &&
                    this.hasGroupPermission("dispatch") &&
                    (!this.excluded || !this.excluded.addResource),
            },
            {
                title: this.$t("jobFabScheduleMe"),
                click: () => {
                    this.scrollIntoView(this.$refs.resources);
                    (this.$refs.resources as any).scheduleResource(this.me)
                },
                if: () => !this.isJobClosed && this.multipleResources && this.canScheduleSelf() && !this.isScheduled(this.me),
            },
            {
                title: this.$t("jobFabItem"),
                click: () => {
                    this.scrollIntoView(this.$refs.items);
                    (this.$refs.items as any)?._setupState?.addItem()
                },
                if: () => !this.isJobClosed && (this.editable || this.myResource) && (!this.excluded || !this.excluded.items),
            },
            {
                title: this.$t("jobFabForm"),
                click: () => {
                    this.scrollIntoView(this.$refs.forms);
                    (this.$refs.forms as any).addForm()
                },
                if: () => !this.isJobClosed && (this.hasPermission("create_forms") || this.editable) && (!this.excluded || !this.excluded.forms),
            },
            {
                title: this.$t("jobFabNote"),
                click: () => {
                    this.scrollIntoView(this.$refs.notes);
                    (this.$refs.notes as any).addNote()
                },
                if: () => !this.isJobClosed && (this.editable || this.myResource),
            },
            {
                title: this.$t("jobFabDocument"),
                click: () => {
                    this.scrollIntoView(this.$refs.documents);
                    (this.$refs.documents as any).addDocument()
                },
                if: () => !this.isJobClosed && ((this.editable || this.myResource) && (!this.excluded || !this.excluded.documents)),
            },
            {
                title: this.$t("jobFabTakePhoto"),
                click: () => (this.$refs.documents as any).takePhoto(),
                if: () => !this.isJobClosed && ((this.editable || this.myResource) && (!this.excluded || !this.excluded.documents)),
            },
            /*
            {
                title: this.$t("jobFabSignature"),
                click: () => {
                    this.scrollIntoView(this.$refs.signature);
                    (this.$refs.signature as any).sign()
                },
                if: () => !this.isJobClosed && ((this.editable || this.myResource) && (!this.excluded || !this.excluded.signature)),
            },
            {
                title: this.$t("remoteSignature"),
                click: () => (this.$refs.remoteSignature as any).sign(),
                if: () => !this.isJobClosed && ((this.editable || this.myResource) && (!this.excluded || !this.excluded.signature)),
            },
            */
            {
                title: this.$t("jobFabSignature"),
                click: () => this.sign(),
                if: () => !this.isJobClosed && ((this.editable || this.myResource) && (!this.excluded || !this.excluded.signature)),
            },
            {
                title: this.$t("jobFabTransportDocument"),
                click: () => (this.$refs.transportDocuments as any).addTransportDocument(),
                if: () => !this.isJobClosed && ((this.editable || this.myResource) && this.canCreateTransportDocuments()),
            }
        ]
    }

    scrollIntoView(ref: any) {
        ref && ref.$el && (ref.$el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" })
    }

    get model() {
        return this.job
    }
    set model(updatedJob: any) {
        this.job = updatedJob
    }
    get resources() {
        return this.job ? this.job.resources : []
    }

    get editable(): boolean {
        // insert unneccesarily complex authorization logic here
        return !this.model.deleted && !this.isJobClosed && this.me.id &&
            (this.me.permissions.includes("edit_all_data")
                || this.job.creatorId == this.me.id
                || this.job.ordererId == this.me.id
                || util.hasGroupPermission(this.me, this.job.groupUuid, "admin_jobs")
            )
    }

    get isJobClosed(): boolean {
        return this.jobState === "CLOSED" || this.job.attestedTimeStamp != null
    }

    get isPublishable(): boolean {
        return (
            this.isJobClosed &&
            (this.me.permissions!.includes("edit_all_data") || this.me.permissions.includes("attest_jobs"))
        )
    }

    get hasBeenInvoiced(): boolean {
        return this.job?.invoicedTimeStamp
    }

    get multipleResources(): boolean {
        return this.job?.autoAddResources
    }

    get showTransportDocuments(): boolean {
        return this.me && this.me.hasFeature && this.me.hasFeature('transport_documents')
            && this.me.hasGroupPermission && this.me.hasGroupPermission(this.model.groupUuid, 'view_transport_documents')
    }

    canCreateTransportDocuments(): boolean {
        var availableWasteItems = this.getWasteItems()
        return availableWasteItems && availableWasteItems.length > 0
            && (
                this.me.hasPermission('view_all_data')
                || (
                    this.me.hasFeature("transport_documents")
                    && this.me.hasGroupPermission(this.model.groupUuid, "create_transport_documents")
                )
            )
    }

    getWasteItems() {
        var wasteItems = this.job?.items.filter((item: any) => item.hazardousWaste)
        if (this.job?.transportDocuments) {
            var transportDocumentWasteItemIds = this.job?.transportDocuments.reduce((acc: any, document: any) => {
            return acc.concat(document.wasteItems.map((item: any) => item.itemId))
            }, [])
            wasteItems = wasteItems.filter((item: any) => !transportDocumentWasteItemIds.includes(parseInt(item.id)))
        }
        return wasteItems.map((item: any) => {
            return {
                id: item.id,
                description: item.description,
                quantity: item.quantity,
                unit: item.unit
            }
        })
    }

    get scheduledTime(): string {
        var startTime: any = this.model.startTime && new Date(this.model.startTime)
        var endTime: any = this.model.endTime && new Date(this.model.endTime)

        var startDate: string = startTime && dateFormat.datePart(startTime, null)
        var endDate: string = endTime && dateFormat.datePart(endTime, null)
        var startHours: string = startDate && dateFormat.time(startTime)
        var endHours: string = endTime && dateFormat.time(endTime)

        var sameDate: boolean = startDate != null && endDate != null && startDate == endDate

        var returner: string = startDate
        if (startDate && !this.model.allDay) {
            if (sameDate) {
                returner += " &nbsp&nbsp&nbsp " + startHours + " — " + endHours
            } else if (endDate) {
                returner += " " + startHours
                returner +=" &nbsp — &nbsp <span class='datespan'>" + endDate + " " + endHours + "</span>" // non-breakable datespan, for readable linebreak on smaller screens
            } else {
                returner += " " + startHours
            }
        } else if (startDate && endDate && !sameDate)
            returner += " &nbsp — &nbsp <span class='datespan'>" + endDate + "</span>"
        if (!startDate && endDate) {
            returner = "<i>" + this.$t('startTimeMissing') + "</i> &nbsp — &nbsp " + endDate + (this.model.allDay ? "" : " " + endHours)
        }
        return returner
    }

    get jobState(): string {
        return this.job?.state
    }

    get isDraft(): boolean {
        return this.jobState == "DRAFT"
    }

    get attested(): boolean {
        return this.job?.attestedTimeStamp
    }

    get smsActive(){
        return this.groupConfiguration?.enable_textmessages === "true"
    }

    get showJobState(): boolean {
        return (
            this.isDraft ||
            this.isJobClosed ||
            !this.myResource ||
            (this.myResource &&
                this.resources &&
                this.resources.filter((resource: any) => resource && resource.userId).length > 1)
        )
    }

    get loading() {
        return (
            !this.wsUpdate &&
            (this.$apollo.queries.job.loading || this.$apollo.queries.me.loading || this.$apollo.queries.groups.loading)
        )
    }

    /*
    get showReportWorkButton() {
        if (!this.myResource || !this.myResource.state) return false
        if (this.isJobClosed) return false
        if (
            this.stateAsEnum(this.myResource.state) == WorkState.DAY_FINISHED &&
            this.job.toBeReported &&
            !this.job.stateTracked
        )
            return true
        if (
            this.stateAsEnum(this.myResource.state) == WorkState.FINISHED &&
            this.job.toBeReported &&
            !this.hasReported(this.me.id)
        )
            return true
        return false
    }
    */

    get remoteSignatureDefaultMethod(): any {
        return this.groupConfiguration?.remote_signature_default_method
    }

    get groupConfiguration(): any {
        if (!this.groups || !this.job || !this.job.groupUuid) return false
        var group = this.groups?.find((group) => group.uuid === this.job.groupUuid)
        return group && group.configuration && JSON.parse(group.configuration)
    }

    get hideInternalContractFields(): any {
        return this.contract && this.isContractSupplier && this.contract?.shareInternalInformationWithSupplier === false
    }

    get isContractSupplier(): any {
        return this.contract && this.contract?.supplierGroupUuid == this.job?.groupUuid
    }

    get showEndCustomer(): boolean {
        return this.job?.contractInfo?.endCustomerName && (this.contract?.shareInternalInformationWithSupplier || !this.job.internalFields || this.job.internalFields?.indexOf('customer') <= -1)
    }

    get endCustomerLink(): string | null {
        if (this.job?.contractInfo?.endCustomerUuid) return '/customer/' + this.job.contractInfo.endCustomerUuid
        return null
    }

    get showEndCustomerProject(): boolean {
        return this.job?.contractInfo?.endCustomerProjectName && (this.contract?.shareInternalInformationWithSupplier || !this.job.internalFields || this.job.internalFields?.indexOf('customerProject') <= -1)
    }

    get endCustomerProjectLink(): string | null {
        if (this.job?.contractInfo?.endCustomerUuid && this.job?.contractInfo?.endCustomerProjectUuid) return '/customer/' + this.job.contractInfo.endCustomerUuid + '/projects/' + this.job.contractInfo.endCustomerProjectUuid
        return null
    }

    get ordererName(): string {
        return this.job?.ordererFullName || this.user?.fullName || this.user?.userName || ''
    }

    get ordererId(): string {
        return this.job?.ordererId ? this.job.ordererId.toString() : (this.job.orderOrdererId ? this.job.orderOrdererId.toString() : (this.job.creatorId ? this.job.creatorId.toString() : this.me.id))
    }

    get jobContact() {
        if (this.job?.contacts && this.job.contacts.length > 0)
            return this.job.contacts[0]
        return null
    }

    myResource?: any = null
    @Watch("job")
    @Watch("job.resources")
    @Watch("me")
    setMyResource() {
        var me = this.me
        if (me && me.id && this.job && this.job.resources) {
            this.myResource = this.job.resources.find((resource: any) => {
                return me.id === resource.userId
            })
        }
    }

    oldResourceStates: string[] = []
    get resourceStates() {
        var states = []
        for (var resource of this.resources) states.push(resource.state)
        if (states.length != 0 && this.oldResourceStates.length != 0)
            this.updateResourceStates(states, this.oldResourceStates)
        this.oldResourceStates = states
        return states
    }

    updateResourceStates(states: string[], oldStates: string[]) {
        const jobUuid = this.job.uuid
        for (var i = 0; i < this.resources.length; i++) {
            if (states[i] != oldStates[i]) {
                if (!this.resources[i].userId) {
                    continue;
                }
                var variables = {
                    uuid: this.job.uuid,
                    state: this.resources[i].state,
                    userId: this.resources[i].userId,
                }
                return this.$apollo
                    .mutate({
                        mutation: SetUserState,
                        variables: variables,
                        update(proxy, response) {
                            // Invalidate cache - TODO there is probably a better way?
                            // taken from WorkReportEdit.vue, modified for jobs
                            const hackProxy = proxy as any
                            Object.keys(hackProxy.data.data).forEach((key) => {
                                if (key.match(/(myJobs|myWorkStats|myCurrentJob)/) || jobUuid && key.indexOf('JobListItem:' + jobUuid) >= 0) hackProxy.data.delete(key)
                            })
                        },
                    })
                    .then(async (response: any) => {})
            }
        }
    }

    updateItem(item: any) {
        delete item.__typename
        this.savingItems = true
        return this.$apollo.mutate({
            mutation: UpdateJobItem,
            variables: {
                jobUuid: this.uuid,
                input: item,
            },
            update: (proxy, response) => {
                console.log('jobItem was updated', response.data.updateJobItem)
                this.savingItems = false
            }
        })
        .then(async (response: any) => {
            if (this.$apollo.queries.job && typeof this.$apollo.queries.job.refetch == "function")
                this.$apollo.queries.job.refetch()
        });
    }

    deleteItem(item: any) {
        this.savingItems = true
        return this.$apollo.mutate({
            mutation: DeleteJobItem,
            variables: {
                jobUuid: this.uuid,
                itemUuid: item.uuid
            },
            update: (proxy, response) => {
                this.savingItems = false
            },
        })
    }

    hasPermission(permission: string) {
        if (this.me && this.me.permissions) {
            return this.me.permissions.includes(permission)
        }
        return false
    }

    hasGroupPermission(permission: string) {
        var membership = this.getGroupMembershipForJob()
        if (membership && membership.permissions) {
            return this.getGroupMembershipForJob().permissions.includes(permission)
        }
        return false
    }

    getGroupMembershipForJob() {
        if (this.me && this.me.groupMemberships && this.job && this.job.groupUuid) {
            return this.me.groupMemberships.find((membership: any) => {
                return membership.groupUuid === this.job.groupUuid
            })
        }
        return null
    }

    hasMultipleActiveGroups() {
        return this.me && this.me.groupMemberships && this.me.groupMemberships.filter((m: any) => m.active).length > 1
    }

    hasReported(userId: string): boolean {
        for (let i = 0; i < this.workReportsForJob.length; i++) {
            if (this.workReportsForJob[i].userId == userId) return true
        }
        return false
    }

    canEditResourceAsset() {
        return (
            (this.isScheduled(this.me) && this.hasGroupPermission("create_jobs")) || this.hasGroupPermission("dispatch")
        )
    }

    clearErrorRequired(field: string) {
        this.errorRequiredFields = this.errorRequiredFields.filter((e) => e !== field)
    }

    get errorRequired(): any {
        console.log(
            "errorRequired ",
            this.errorRequiredFields
                ? this.errorRequiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
                : null
        )
        return this.errorRequiredFields
            ? this.errorRequiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }

    verifyRequired(input: any) {
        var errorRequiredFields: string[] = []
        if (input.requiredFields) {
            if (input.requiredFields.includes("title")) if (!input["title"]) errorRequiredFields.push("title")
            if (input.requiredFields.includes("activity"))
                if (!input["activityId"]) errorRequiredFields.push("activity")
            if (input.requiredFields.includes("customer"))
                if (!input["customerUuid"]) errorRequiredFields.push("customer")
            if (input.requiredFields.includes("customerProject"))
                if (!input["customerProjectUuid"]) errorRequiredFields.push("customerProject")
            if (input.requiredFields.includes("description"))
                if (!input["description"]) errorRequiredFields.push("description")
            if (input.requiredFields.includes("group")) if (!input["groupUuid"]) errorRequiredFields.push("group")
            if (input.requiredFields.includes("startTime"))
                if (!input["startTime"]) errorRequiredFields.push("startTime")
            if (input.requiredFields.includes("endTime")) if (!input["endTime"]) errorRequiredFields.push("endTime")
            if (input.requiredFields.includes("labels"))
                if (!input["labels"] || input["labels"].length == 0) errorRequiredFields.push("labels")
            if (input.requiredFields.includes("vehicle")) if (!input["vehicleId"]) errorRequiredFields.push("vehicle")
        }
        if (this) this.errorRequiredFields = errorRequiredFields
        if (errorRequiredFields.length == 0) return true
        return false
    }

    async refresh() {
        this.fetchError = null
        console.log("refreshing job form")
        var promises: Promise<any>[] = []
        if (this.$apollo.queries.job && typeof this.$apollo.queries.job.refetch == "function") {
            promises.push(
                new Promise((res, rej) => {
                    res(this.$apollo.queries.job.refetch())
                })
            )
        }
        if (
            this.$apollo.queries.workReportsForJob &&
            typeof this.$apollo.queries.workReportsForJob.refetch == "function"
        )
            promises.push(
                new Promise((res, rej) => {
                    res(this.$apollo.queries.workReportsForJob.refetch())
                })
            )
        await Promise.all(promises).then((val) => {
            this.wsUpdate = false
        })
    }

    stateAsEnum(state: string) {
        return WorkState[state as keyof typeof WorkState] as unknown as WorkState
    }

    get options(): NavbarOption[] {
        if (!this.me || !this.me.permissions || !this.model || this.model.deleted) return []

        var options = []
        if (this.editable)
            options.push({
                icon: "edit",
                title: this.$t("jobOptions_editJob"),
                to: `/job/${this.uuid}/edit`,
            })
        if (
            this.me.permissions.includes("report_work") &&
            !this.isJobClosed &&
            !this.isDraft &&
            this.hasGroupPermission("do_work")
        ) {
            options.push({
                title: this.$t("jobOptions_reportWork"),
                to: `/workreport/job/${this.uuid}`,
            })
        }
        if (this.me.permissions.includes("share") && this.hasGroupPermission("share"))
            options.push({
                title: this.$t("jobOptions_shareJob"),
                action: this.shareJob,
            })
        if (this.myResource && !this.isJobClosed)
            options.push({
                title: this.$t("jobOptions_changeMyState"),
                action: this.changeMyState,
            })
        if (this.editable)
            options.push({
                title: this.$t("jobOptions_changeJobState"),
                action: this.changeJobState,
            })
        if (this.me.permissions.includes("create_jobs"))
            options.push({
                title: this.$t("jobOptions_copyJob"),
                action: this.copyJob,
            })
        if (this.isPublishable)
        options.push({
            title: this.$t("jobOptions_openJob"),
            action: this.publishJob,
        })
        if (this.editable && this.hasGroupPermission("admin_jobs"))
            options.push({
                title: this.$t("jobOptions_closeJob"),
                action: this.closeJob,
            })
        return options
    }

    sharing: boolean = false
    shareJob() {
        this.sharing = true
    }

    changingMyState: boolean = false
    changeMyState() {
        this.changingMyState = true
    }

    changingJobState: boolean = false
    changeJobState() {
        this.changingJobState = true
    }

    publishJob() {
        var uuid = this.job.uuid
        var mutation = PublishJob
        var variables = {
            uuid: uuid,
        }
        return this.$apollo
            .mutate({
                mutation: mutation,
                variables: variables,
                update(proxy, response) {
                    // Invalidate cache - TODO there is probably a better way?
                    // taken from WorkReportEdit.vue, modified for jobs
                    const hackProxy = proxy as any
                    Object.keys(hackProxy.data.data).forEach((key) => {
                        if (key.match(/(myJobs|myWorkStats|myCurrentJob)/) || uuid && key.indexOf('JobListItem:' + uuid) >= 0) hackProxy.data.delete(key)
                    })
                },
            })
            .then(async (response: any) => {
                var newState = ""
                for (let key of Object.keys(response.data))
                    if ("state" in response.data[key]) newState = response.data[key]["state"]
                this.job.state = newState
            })
            .catch((error) => {
                console.log(error)
            })
    }

    closeJob() {
        this.showCloseModal = true
    }

    confirmClose(comment: string) {
        var uuid = this.job.uuid
        var mutation = CloseJob
        var variables = {
            uuid: uuid,
            comment: comment
        }
        return this.$apollo
            .mutate({
                mutation: mutation,
                variables: variables,
                update(proxy, response) {
                    // Invalidate cache - TODO there is probably a better way?
                    // taken from WorkReportEdit.vue, modified for jobs
                    const hackProxy = proxy as any
                    Object.keys(hackProxy.data.data).forEach((key) => {
                        if (key.match(/(myJobs|myWorkStats|myCurrentJob)/) || uuid && key.indexOf('JobListItem:' + uuid) >= 0) hackProxy.data.delete(key)
                    })
                },
            })
            .then(async (response: any) => {
                var newState = ""
                for (let key of Object.keys(response.data))
                    if ("state" in response.data[key]) newState = response.data[key]["state"]
                this.job.state = newState
            })
            .catch((error) => {
                console.log(error)
                ;(this as any).$toast.error(this.$t("closeJobFailed"), { icon: 'warning' })
            })
            .finally(() => {
                this.showCloseModal = false
            })
    }

    invalidateCache(proxy: any, response: any) {
        const hackProxy = proxy as any
        Object.keys(hackProxy.data.data).forEach((key) => {
            if (key.match(/(myJobs|myWorkStats|JobListItem)/)) hackProxy.data.delete(key)
        })
    }
    replacedByTemplate: any = null
    handleReplacedFields(event: any) {
        this.replacedByTemplate = event
    }
    get iosWrapperClient() {
        return Vue.prototype.$iosWrapperClient
    }
    get shouldScheduleMe() {
        return "me" in this.$route.params || "me" in this.$route.query
    }

    isScheduled(user: any) {
        return (
            this.model.resources &&
            this.model.resources.find((r: any) => {
                return r.userId == user.id
            })
        )
    }

    copyJob() {
        if (!this.uuid) {
            console.error('no uuid in job detail view??')
            return
        }
        this.$router.push(`/job/${this.uuid}/copy`)
    }

    sign(){
        if (this.job?.remoteSignature?.id) {
            const state = this.job.remoteSignature.state
            if (state == "INITIAL" || state == "OPENED" || state == "PARTIAL" || state == "COMPLETED" || state == "DECLINED"){
                this.$router.push('/remotesignature/'+ this.job.remoteSignature.id)
                return
            }
        } else {
            this.creatingSignature = true
            if (this.job?.signature) (this.$refs.signatureDialog as any)?.changeTab('onScreen')
        }
    }

    get jobPriorityIcon() {
        return this.job.priority == "URGENT" ? "whatshot" : this.job.priority == "HIGH" ? "arrow_upward" : this.job.priority == "LOW" ? "arrow_downward" : "priority_high"
    }

    get documentsSize() {
        if (!this.job.documents || this.job.documents.length == 0) return 0
        return this.job.documents.reduce((total: number, doc: any) => {
            return total + (doc.size || 0)
        }, 0)
    }
}

export enum WorkState {
    UNASSIGNED,
    DECLINED,
    ASSIGNED,
    SCHEDULED,
    ACCEPTED,
    INACTIVE,
    IN_TRANSIT,
    ON_STATION,
    STARTED,
    PAUSED,
    DAY_FINISHED,
    FINISHED,
    CANCELED,
}
